// PendingRequests.jsx - Enhanced version with inline notifications
import React, { useState, useEffect, useContext, useRef } from 'react';
import axios from 'axios';
import SglContext from '../../SglContext';
import './pendingRequests.css';
import { useNavigate } from 'react-router-dom';

// Inline Notification Component (same as TeamManagement)
const InlineNotification = ({ notification, onDismiss }) => {
  if (!notification) return null;

  const getNotificationStyles = () => {
    const typeStyles = {
      success: {
        backgroundColor: '#fdfffe',
        borderColor: '#059669',
        color: '#059669'
      },
      error: {
        backgroundColor: '#fef2f2',
        borderColor: '#dc2626',
        color: '#dc2626'
      },
      info: {
        backgroundColor: '#f0f9ff',
        borderColor: '#0369a1',
        color: '#0369a1'
      },
      warning: {
        backgroundColor: '#fffbeb',
        borderColor: '#d97706',
        color: '#d97706'
      }
    };

    return typeStyles[notification.type] || typeStyles.info;
  };

  const getIcon = () => {
    switch (notification.type) {
      case 'success': return '✓';
      case 'error': return '✕';
      case 'warning': return '⚠';
      case 'info': return 'ℹ';
      default: return 'ℹ';
    }
  };

  return (
    <div 
      className="inline-notification"
      style={{
        ...getNotificationStyles(),
        padding: '8px 16px',
        margin: '10px 16px 10px 16px',
        borderRadius: '6px',
        border: '1px solid',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
        fontSize: '14px',
        fontWeight: '500'
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
        <span style={{ fontSize: '16px', lineHeight: '1' }}>
          {getIcon()}
        </span>
        <span style={{ lineHeight: '1.4' }}>
          {notification.message}
        </span>
      </div>
      {onDismiss && (
        <button
          onClick={onDismiss}
          style={{
            background: 'none',
            border: 'none',
            color: 'inherit',
            fontSize: '18px',
            cursor: 'pointer',
            padding: '10px',
            lineHeight: '1',
            boxSizing: 'border-box',
            marginTop: '-5px'
          }}
        >
          ×
        </button>
      )}
    </div>
  );
};

// Notification Manager Hook
const useInlineNotifications = () => {
  const [notification, setNotification] = useState(null);

  const showNotification = (message, type = 'info', duration = 5000) => {
    const notificationObj = { message, type, timestamp: Date.now() };
    setNotification(notificationObj);
    
    if (duration > 0) {
      setTimeout(() => {
        setNotification(prev => 
          prev && prev.timestamp === notificationObj.timestamp ? null : prev
        );
      }, duration);
    }
    
    return notificationObj.timestamp;
  };

  const dismissNotification = () => {
    setNotification(null);
  };

  const showSuccess = (message, duration = 5000) => showNotification(message, 'success', duration);
  const showError = (message, duration = 6000) => showNotification(message, 'error', duration);
  const showInfo = (message, duration = 4000) => showNotification(message, 'info', duration);
  const showWarning = (message, duration = 5000) => showNotification(message, 'warning', duration);

  return {
    notification,
    showNotification,
    showSuccess,
    showError,
    showInfo,
    showWarning,
    dismissNotification
  };
};

const PendingRequests = () => {
  const [requests, setRequests] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('team_invitation');
  const [processingRequest, setProcessingRequest] = useState(null);
  const tabContainerRef = useRef(null);
  const leftIndicatorRef = useRef(null);
  const rightIndicatorRef = useRef(null);
  const Sgl = useContext(SglContext);
  const navigate = useNavigate();

  const { 
    notification, 
    showSuccess, 
    showError, 
    showInfo, 
    showWarning, 
    dismissNotification 
  } = useInlineNotifications();

  // Map request types to user-friendly labels
  const requestTypeLabels = {
    team_invitation: 'Team Invitations',
    guardian_confirmation: 'Guardian Confirmations',
    friend_request: 'Friend Requests',
    event_invitation: 'Event Invitations',
    verify_user: 'User Verification'
  };

  // Tab scroll functionality
  useEffect(() => {
    const updateScrollIndicators = () => {
      if (!tabContainerRef.current || !leftIndicatorRef.current || !rightIndicatorRef.current) return;
      
      const container = tabContainerRef.current;
      const isOverflowing = container.scrollWidth > container.clientWidth;
      const isAtStart = container.scrollLeft <= 1;
      const isAtEnd = container.scrollLeft >= container.scrollWidth - container.clientWidth - 1;

      // Only show indicators if there's overflow
      if (!isOverflowing) {
        leftIndicatorRef.current.classList.remove('visible');
        rightIndicatorRef.current.classList.remove('visible');
      } else {
        leftIndicatorRef.current.classList.toggle('visible', !isAtStart);
        rightIndicatorRef.current.classList.toggle('visible', !isAtEnd);
      }
    };

    const scrollTabs = (direction) => {
      if (!tabContainerRef.current) return;
      const scrollAmount = 150;
      if (direction === 'left') {
        tabContainerRef.current.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
      } else {
        tabContainerRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
      }
    };

    const handleLeftClick = () => scrollTabs('left');
    const handleRightClick = () => scrollTabs('right');

    // Event listeners
    if (leftIndicatorRef.current) {
      leftIndicatorRef.current.addEventListener('click', handleLeftClick);
    }
    if (rightIndicatorRef.current) {
      rightIndicatorRef.current.addEventListener('click', handleRightClick);
    }
    if (tabContainerRef.current) {
      tabContainerRef.current.addEventListener('scroll', updateScrollIndicators);
    }
    window.addEventListener('resize', updateScrollIndicators);

    // Initial check
    setTimeout(updateScrollIndicators, 100);

    return () => {
      if (leftIndicatorRef.current) {
        leftIndicatorRef.current.removeEventListener('click', handleLeftClick);
      }
      if (rightIndicatorRef.current) {
        rightIndicatorRef.current.removeEventListener('click', handleRightClick);
      }
      if (tabContainerRef.current) {
        tabContainerRef.current.removeEventListener('scroll', updateScrollIndicators);
      }
      window.removeEventListener('resize', updateScrollIndicators);
    };
  }, []);

  useEffect(() => {
    loadPendingRequests();
  }, []);

  const loadPendingRequests = async (showLoadingNotification = false) => {
    try {
      setLoading(true);
      setError('');
      
      if (showLoadingNotification) {
        showInfo('Refreshing requests...', 2000);
      }
      
      const response = await axios.get(`${Sgl.nestApiUrl}/user-requests/pending`);
      
      // Group requests by type
      const groupedRequests = response.data.reduce((groups, request) => {
        if (!groups[request.request_type]) {
          groups[request.request_type] = [];
        }
        groups[request.request_type].push(request);
        return groups;
      }, {});
      
      setRequests(groupedRequests);
      
      // Set active tab to first available type
      const availableTypes = Object.keys(groupedRequests);
      if (availableTypes.length > 0 && !availableTypes.includes(activeTab)) {
        setActiveTab(availableTypes[0]);
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to load pending requests';
      setError(errorMessage);
      showError(errorMessage, 8000);
      console.error('Error loading pending requests:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTabSwitch = (requestType) => {
    dismissNotification(); // Clear notifications when switching tabs
    setActiveTab(requestType);
  };

  const handleAcceptRequest = async (requestId, requestType, requestTitle) => {
    try {
      setProcessingRequest(requestId);
      showInfo('Processing request...', 3000);
      
      // Use the generic accept endpoint for all request types
      await axios.post(`${Sgl.nestApiUrl}/user-requests/accept`, {
        requestId
      });
      
      // Refresh requests and user context
      await loadPendingRequests(false);
      if (Sgl && Sgl.getUser) {
        Sgl.getUser();
      }

      if (Sgl.refreshPendingRequestsCount) {
        Sgl.refreshPendingRequestsCount();
      }
            
      // Show success message based on request type
      const successMessages = {
        team_invitation: `Team invitation "${requestTitle}" accepted successfully!`,
        guardian_confirmation: `Guardian confirmation for "${requestTitle}" completed successfully!`,
        friend_request: `Friend request from "${requestTitle}" accepted successfully!`,
        event_invitation: `Event invitation "${requestTitle}" accepted successfully!`
      };
      
      showSuccess(successMessages[requestType] || `Request "${requestTitle}" accepted successfully!`, 6000);
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to accept request';
      showError(errorMessage, 8000);
    } finally {
      setProcessingRequest(null);
    }
  };

  const handleDeclineRequest = async (requestId, requestType, requestTitle) => {
    if (!window.confirm(`Are you sure you want to decline "${requestTitle}"?`)) {
      return;
    }

    try {
      setProcessingRequest(requestId);
      showInfo('Declining request...', 3000);
      
      // Use the generic decline endpoint for all request types
      await axios.post(`${Sgl.nestApiUrl}/user-requests/decline`, {
        requestId
      });
      
      await loadPendingRequests(false);

      if (Sgl.refreshPendingRequestsCount) {
        Sgl.refreshPendingRequestsCount();
      }
      
      // Show success message based on request type
      const successMessages = {
        team_invitation: `Team invitation "${requestTitle}" declined.`,
        guardian_confirmation: `Guardian confirmation "${requestTitle}" declined.`,
        friend_request: `Friend request "${requestTitle}" declined.`,
        event_invitation: `Event invitation "${requestTitle}" declined.`
      };
      
      showSuccess(successMessages[requestType] || `Request "${requestTitle}" declined.`, 5000);
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to decline request';
      showError(errorMessage, 8000);
    } finally {
      setProcessingRequest(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTimeAgo = (dateString) => {
    if (!dateString) return 'N/A';
    const now = new Date();
    const date = new Date(dateString);
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) {
      return 'Just now';
    } else if (diffInHours < 24) {
      return `${diffInHours} hour${diffInHours !== 1 ? 's' : ''} ago`;
    } else {
      const diffInDays = Math.floor(diffInHours / 24);
      if (diffInDays < 7) {
        return `${diffInDays} day${diffInDays !== 1 ? 's' : ''} ago`;
      } else {
        const diffInWeeks = Math.floor(diffInDays / 7);
        return `${diffInWeeks} week${diffInWeeks !== 1 ? 's' : ''} ago`;
      }
    }
  };

  const isRequestExpired = (expiresAt) => {
    if (!expiresAt || expiresAt.startsWith('0000')) return false;
    return new Date(expiresAt) < new Date();
  };

  const renderRequestCard = (request) => {
    const isProcessing = processingRequest === request.sgl_id;
    const expired = isRequestExpired(request.expires_at);
    const isGuardianConfirmation = request.request_type === 'guardian_confirmation';
    const isUserVerification = request.request_type === 'verify_user';
    
    return (
      <div key={request.sgl_id} className={`request-card ${expired ? 'expired' : ''}`}>
        <div className="request-header">
          <div className="request-title-section">
            <h4 className="request-title">
              {request.title}
              {expired && request.expires_at && !request.expires_at.startsWith('0000') && (
                <span className="expired-badge">Expired</span>
              )}
            </h4>
          </div>
          <div className="request-meta">
            <span className="request-time">{formatTimeAgo(request.created_at)}</span>
              {request.expires_at && !request.expires_at.startsWith('0000') && !expired && (
                <span className="request-expiry">
                  Expires: {formatDate(request.expires_at)}
                </span>
              )}
              {expired && request.expires_at && !request.expires_at.startsWith('0000') && (
                <span className="request-expiry expired-text">
                  Expired: {formatDate(request.expires_at)}
                </span>
              )}
          </div>
        </div>
        
        {request.message && (
          <div className="request-message">
            {request.message}
          </div>
        )}
        
        {request.sender_name && (
          <div className="request-sender">
            From: <strong>{request.sender_name}</strong>
          </div>
        )}
        
        <div className="request-actions">
          {isGuardianConfirmation ? (
          // Show View Request button for guardian confirmations
          <button
            className="btn-view-request new-gray-button"
            // onClick={() => window.open(request.accept_url, "_self")}
            onClick={() => navigate(request.accept_url)}
            disabled={expired}
          >
            View Request
          </button>
        ) : isUserVerification ? (
          // Show Verify button for user verification
          <button
            className="btn-accept"
            onClick={() => navigate('/verify-login')}
            // onClick={() => (window.location.href = '/verify-login')}
          >
            Verify
          </button>
        ) : (
          // Show Accept/Decline buttons for other request types
          <>
          <button
            className="btn-accept"
            onClick={() => handleAcceptRequest(request.sgl_id, request.request_type, request.title)}
            disabled={isProcessing || expired}
          >
            {isProcessing ? (
              <>
                <span className="processing-spinner"></span>
                Processing...
              </>
            ) : (
              'Accept'
            )}
          </button>
          <button
            className="btn-decline"
            onClick={() => handleDeclineRequest(request.sgl_id, request.request_type, request.title)}
            disabled={isProcessing || expired}
          >
            {expired ? 'Remove' : 'Decline'}
          </button>
          </>
        )}
        </div>
      </div>
    );
  };

  const renderTabContent = (requestType) => {
    const typeRequests = requests[requestType] || [];
    
    if (typeRequests.length === 0) {
      return (
        <div className="empty-state">
          <div className="empty-state-icon">
            {requestType === 'team_invitation' && '👥'}
            {requestType === 'guardian_confirmation' && '👨‍👩‍👧‍👦'}
            {requestType === 'friend_request' && '🤝'}
            {requestType === 'event_invitation' && '📅'}
            {requestType === 'verify_user' && '✓'}
          </div>
          <div className="empty-state-text">
            No pending {requestTypeLabels[requestType].toLowerCase()}
          </div>
        </div>
      );
    }

    return (
      <div className="requests-list">
        {typeRequests.map(request => renderRequestCard(request))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="pending-requests">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading your requests...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pending-requests">
        <div className="error-container">
          <p>Error: {error}</p>
          <button onClick={() => loadPendingRequests(true)} className="retry-btn">
            Retry
          </button>
        </div>
      </div>
    );
  }

  const availableRequestTypes = Object.keys(requests);
  const totalRequests = Object.values(requests).flat().length;

  if (totalRequests === 0) {
    return (
      <div className="pending-requests">
        <div className="requests-header">
          <h2>Your Pending Requests</h2>
        </div>
        <InlineNotification 
          notification={notification} 
          onDismiss={dismissNotification} 
        />
        <div className="no-requests">
          <div className="no-requests-icon">📬</div>
          <h3>All caught up!</h3>
          <p>You don't have any pending requests at the moment.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pending-requests">
      <div className="requests-header">
        <h2>Your Pending Requests</h2>
      </div>

      {/* Inline notifications appear after header */}
      <InlineNotification 
        notification={notification} 
        onDismiss={dismissNotification} 
      />

      <div className="requests-tabs">
        <div className="scroll-indicator left" ref={leftIndicatorRef}></div>
        <div className="tab-scroll-container" ref={tabContainerRef}>
          <div className="tab-buttons">
            {availableRequestTypes.map(requestType => {
              const count = requests[requestType]?.length || 0;
              return (
                <button
                  key={requestType}
                  className={`tab-button ${activeTab === requestType ? 'active' : ''}`}
                  onClick={() => handleTabSwitch(requestType)}
                >
                  <span className="tab-label">{requestTypeLabels[requestType]}</span>
                  <span className="tab-count">{count}</span>
                </button>
              );
            })}
          </div>
        </div>
        <div className="scroll-indicator right" ref={rightIndicatorRef}></div>
      </div>

      <div className="tab-content">
        {renderTabContent(activeTab)}
      </div>
    </div>
  );
};

export default PendingRequests;